import React, { useEffect, useMemo } from "react";
import ScopeBar from "./ScopeBar";
import ReportActions from "./ReportActions";
import PrintableReportHeader from "./PrintableReportHeader";
import { useTenantScope } from "../scope/TenantScopeContext";
import "../styles/tenantScope.css";

/*
 * Compatibility sentinel retained for the Phase 3 scope/fetch framework.
 * The V2 marker identifies the UI-only layout corrective.
 */
export const WP_GLOBAL_SCOPE_SHELL_VERSION =
  "WP_R5_PHASE3_GLOBAL_SCOPE_SHELL_V1";

export const WP_GLOBAL_SCOPE_LAYOUT_VERSION =
  "WP_R5_PHASE3_UI_LAYOUT_RESTORE_V1";

function moduleNameFromPath(pathname) {
  const clean = String(pathname || "/dashboard")
    .replace(/^\/+|\/+$/g, "")
    .split("/")[0];

  if (!clean) return "Dashboard";

  return clean
    .split(/[-_]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function shouldHide(pathname) {
  const path = String(pathname || "").toLowerCase();

  return (
    path === "/login" ||
    path.startsWith("/login/") ||
    path === "/aup" ||
    path.startsWith("/aup/")
  );
}

function GlobalScopeShell() {
  const scope = useTenantScope();
  const pathname = window.location.pathname;

  const moduleName = useMemo(
    () => moduleNameFromPath(pathname),
    [pathname]
  );

  useEffect(() => {
    if (scope.tokenPresent) {
      scope.refreshScope().catch(() => {});
    }

    // Initial authenticated scope hydration only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!scope.tokenPresent || shouldHide(pathname)) {
    return null;
  }

  const applyAndRefresh = async () => {
    window.location.reload();
  };

  const companyLabel =
    scope.selectedCompany?.company_name ||
    scope.selectedCompany?.name ||
    (scope.companyId ? `Company ${scope.companyId}` : "Company");

  const platformLabel =
    scope.platform === "all"
      ? "All platforms"
      : scope.platform === "macos"
      ? "macOS"
      : scope.platform === "ios"
      ? "iOS / iPadOS"
      : scope.platform
      ? scope.platform.charAt(0).toUpperCase() + scope.platform.slice(1)
      : "All platforms";

  const deviceLabel =
    scope.selectedDevice?.hostname ||
    scope.selectedDevice?.device_name ||
    (scope.deviceUuid ? "Selected device" : "All devices");

  return (
    <>
      <PrintableReportHeader
        moduleName={moduleName}
        subtitle="Wireless Press filtered operational report"
      />

      <details
        className="wp-global-scope-shell wp-global-scope-drawer no-print"
        data-wp-scope-version={WP_GLOBAL_SCOPE_SHELL_VERSION}
        data-wp-layout-version={WP_GLOBAL_SCOPE_LAYOUT_VERSION}
      >
        <summary
          className="wp-global-scope-drawer-trigger"
          title="Open dashboard filters and report actions"
        >
          <span className="wp-global-scope-drawer-title">
            Filters &amp; Reports
          </span>

          <span className="wp-global-scope-drawer-summary">
            {companyLabel} · {platformLabel} · {deviceLabel}
          </span>
        </summary>

        <div className="wp-global-scope-drawer-panel">
          <div className="wp-global-scope-heading">
            <div>
              <div className="wp-global-scope-kicker">
                Reporting Scope
              </div>
              <strong>{moduleName}</strong>
            </div>

            <ReportActions moduleName={moduleName} />
          </div>

          <ScopeBar
            showDevice
            showPlatform
            showDateRange
            showStatus
            showSeverity
            onRefresh={applyAndRefresh}
          />

          <div className="wp-global-scope-note">
            Filters are applied only when the API route advertises the
            matching query parameter. Company coverage is broad but not
            universal; device coverage is limited; date filtering is not
            yet exposed by the current API. Print, Save as PDF, and CSV
            operate on the currently visible report data.
          </div>
        </div>
      </details>
    </>
  );
}

export default GlobalScopeShell;
